/* LUXIRE THEME JS v32 - Core runtime
   Modules: Utils, Cart, CartDrawer, StickyHeader, MobileMenu, Search,
   ProductGallery, Slideshow, QuantitySelector, Customizer, HelpPanel, FAQ, Filters
   Product pages also load: product-page.js, help-panel-data.js, measurement-help.js */
(function(){
'use strict';
var Luxire = window.Luxire = window.Luxire || {};

/* ── Utilities ── */
Luxire.Utils = {
  formatMoney: function(cents) { return '$' + (cents / 100).toFixed(2); },
  debounce: function(fn, delay) {
    var timeout;
    return function() {
      var ctx = this, args = arguments;
      clearTimeout(timeout);
      timeout = setTimeout(function() { fn.apply(ctx, args); }, delay || 300);
    };
  }
};

/* ── Cart ── */
Luxire.Cart = {
  async get() { return (await fetch('/cart.js')).json(); },
  async add(items) {
    var r = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: items })
    });
    var data = await r.json();
    this.updateCount();
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: data }));
    return data;
  },
  async updateCount(c) {
    if (c === undefined) { var cart = await this.get(); c = cart.item_count; }
    document.querySelectorAll('[data-cart-count]').forEach(function(el) {
      el.textContent = c;
      el.style.display = c > 0 ? 'flex' : 'none';
    });
  }
};

/* ── Cart Drawer ── */
Luxire.CartDrawer = {
  el: null,
  init: function() {
    this.el = document.querySelector('[data-cart-drawer]');
    if (!this.el) return;
    var self = this;
    document.querySelectorAll('[data-cart-open]').forEach(function(btn) {
      btn.addEventListener('click', function(e) { e.preventDefault(); self.open(); });
    });
    this.el.querySelectorAll('[data-cart-close]').forEach(function(btn) {
      btn.addEventListener('click', function() { self.close(); });
    });
    document.addEventListener('keydown', function(e) {
      if (e.key === 'Escape' && self.el && self.el.getAttribute('aria-hidden') === 'false') self.close();
    });
    document.addEventListener('cart:updated', function() { self.refresh(); });
  },
  open: function() {
    if (!this.el) return;
    this.refresh();
    this.el.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
  },
  close: function() {
    if (!this.el) return;
    this.el.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
  },
  refresh: async function() {
    if (!this.el) return;
    try {
      var res = await fetch('/?sections=cart-drawer');
      var data = await res.json();
      var html = new DOMParser().parseFromString(data['cart-drawer'], 'text/html');
      var newContent = html.querySelector('[data-cart-content]');
      var current = this.el.querySelector('[data-cart-content]');
      if (newContent && current) current.innerHTML = newContent.innerHTML;
    } catch (e) { console.error('Cart refresh error:', e); }
  }
};

/* ── Sticky Header ── */
Luxire.StickyHeader = {
  init: function() {
    var header = document.querySelector('.lx-header');
    if (!header) return;
    window.addEventListener('scroll', Luxire.Utils.debounce(function() {
      header.classList.toggle('is-scrolled', window.scrollY > 50);
    }, 10), { passive: true });
  }
};

/* ── Mobile Menu ── */
/* NOTE: Mobile menu is now handled directly in header.liquid with inline onclick handlers.
   This code is disabled to prevent double-firing of events. */
Luxire.MobileMenu = {
  init: function() {
    // Disabled - mobile menu uses inline onclick handlers in header.liquid
    return;
  }
};

/* ── Search Modal ── */
Luxire.Search = {
  init: function() {
    var modal = document.querySelector('[data-search-modal]');
    if (!modal) return;
    document.querySelectorAll('[data-search-open]').forEach(function(btn) {
      btn.addEventListener('click', function() {
        modal.setAttribute('aria-hidden', 'false');
        var input = modal.querySelector('input');
        if (input) input.focus();
        document.body.style.overflow = 'hidden';
      });
    });
    document.querySelectorAll('[data-search-close]').forEach(function(btn) {
      btn.addEventListener('click', function() {
        modal.setAttribute('aria-hidden', 'true');
        document.body.style.overflow = '';
      });
    });
  }
};

/* ── Product Gallery ── */
Luxire.ProductGallery = {
  init: function() {
    document.querySelectorAll('.product-gallery').forEach(function(g) {
      var main = g.querySelector('[data-main-image]');
      g.querySelectorAll('.product-gallery__thumb').forEach(function(t) {
        t.addEventListener('click', function() {
          g.querySelectorAll('.product-gallery__thumb').forEach(function(x) { x.classList.remove('is-active'); });
          t.classList.add('is-active');
          if (main) main.src = t.dataset.src || t.dataset.thumbSrc;
        });
      });
    });
  }
};

/* ── Slideshow ── */
Luxire.Slideshow = {
  init: function() {
    document.querySelectorAll('[data-slideshow]').forEach(function(slideshow) {
      var slides = slideshow.querySelectorAll('.slideshow__slide');
      var dots = slideshow.querySelectorAll('.slideshow__dot');
      if (slides.length < 2) return;
      var current = 0, autoplay = parseInt(slideshow.dataset.autoplay) * 1000 || 0, interval;
      var goTo = function(i) {
        slides.forEach(function(s, idx) { s.classList.toggle('is-active', idx === i); });
        dots.forEach(function(d, idx) { d.classList.toggle('is-active', idx === i); });
        current = i;
      };
      var next = function() { goTo((current + 1) % slides.length); };
      var prev = function() { goTo((current - 1 + slides.length) % slides.length); };
      var resetTimer = function() { if (autoplay) { clearInterval(interval); interval = setInterval(next, autoplay); } };
      var prevBtn = slideshow.querySelector('[data-slide-prev]');
      var nextBtn = slideshow.querySelector('[data-slide-next]');
      if (prevBtn) prevBtn.addEventListener('click', function() { prev(); resetTimer(); });
      if (nextBtn) nextBtn.addEventListener('click', function() { next(); resetTimer(); });
      dots.forEach(function(dot, i) { dot.addEventListener('click', function() { goTo(i); resetTimer(); }); });
      if (autoplay) interval = setInterval(next, autoplay);
    });
  }
};

/* ── Quantity Selector ── */
Luxire.QuantitySelector = {
  init: function() {
    document.querySelectorAll('.quantity-selector').forEach(function(sel) {
      var input = sel.querySelector('input');
      var dec = sel.querySelector('[data-action="decrease"]');
      var inc = sel.querySelector('[data-action="increase"]');
      if (dec) dec.addEventListener('click', function() { var v = parseInt(input.value) || 1; if (v > 1) input.value = v - 1; });
      if (inc) inc.addEventListener('click', function() { input.value = (parseInt(input.value) || 0) + 1; });
    });
  }
};

/* ── Customizer (Product page option selection + submit) ── */
Luxire.Customizer = {
  container: null, basePrice: 0, selections: {},

  init: function() {
    this.container = document.querySelector('[data-customizer]');
    if (!this.container) return;
    var pj = this.container.querySelector('[data-product-json]');
    if (pj) try { this.basePrice = JSON.parse(pj.textContent).price || 0; } catch (e) {}
    this.bindEvents();
    this.loadInitial();
    this.updatePrice();
  },

  bindEvents: function() {
    var self = this;
    this.container.querySelectorAll('.option-card').forEach(function(c) { c.addEventListener('click', function() { self.selectCard(c); }); });
    this.container.querySelectorAll('.option-row').forEach(function(r) { r.addEventListener('click', function() { self.selectRow(r); }); });
    this.container.querySelectorAll('.size-field select, .size-field input').forEach(function(s) { s.addEventListener('change', function(e) { self.onSize(e); }); });
    this.container.querySelectorAll('.measurement-tab').forEach(function(t) { t.addEventListener('click', function() { self.switchTab(t); }); });
    this.container.querySelectorAll('[data-help]').forEach(function(b) { b.addEventListener('click', function(e) { e.preventDefault(); Luxire.HelpPanel.open(b.dataset.help); }); });
    this.container.querySelectorAll('.field-help').forEach(function(b) { b.addEventListener('click', function(e) { e.preventDefault(); Luxire.HelpPanel.open(b.dataset.help); }); });
    this.container.querySelectorAll('.notes-chip').forEach(function(c) { c.addEventListener('click', function() { self.addChip(c.textContent); }); });
    var form = this.container.querySelector('form');
    if (form) form.addEventListener('submit', function(e) { self.submit(e); });
  },

  selectCard: function(c) {
    var sec = c.closest('.customizer__section'), opt = sec ? sec.dataset.option : null;
    sec.querySelectorAll('.option-card').forEach(function(x) { x.classList.remove('is-selected'); });
    c.classList.add('is-selected');
    var inp = c.querySelector('input'); if (inp) inp.checked = true;
    if (opt) this.selections[opt] = { value: c.dataset.value, label: (c.querySelector('.option-card__label') || {}).textContent || c.dataset.value, price: parseFloat(c.dataset.price) || 0 };
    this.updatePrice();
  },

  selectRow: function(r) {
    var sec = r.closest('.customizer__section'), opt = sec ? sec.dataset.option : null;
    sec.querySelectorAll('.option-row').forEach(function(x) { x.classList.remove('is-selected'); });
    r.classList.add('is-selected');
    var inp = r.querySelector('input'); if (inp) inp.checked = true;
    if (opt) this.selections[opt] = { value: r.dataset.value, label: (r.querySelector('.option-row__title') || {}).textContent || r.dataset.value, price: parseFloat(r.dataset.price) || 0 };
    this.updatePrice();
  },

  onSize: function(e) { this.selections[e.target.name] = { value: e.target.value, label: e.target.value, price: 0 }; },

  switchTab: function(t) {
    var c = t.closest('.customizer__section');
    c.querySelectorAll('.measurement-tab').forEach(function(x) { x.classList.remove('is-active'); });
    c.querySelectorAll('.measurement-content').forEach(function(x) { x.classList.remove('is-active'); x.querySelectorAll('input[type="hidden"]').forEach(function(h) { h.disabled = true; }); });
    t.classList.add('is-active');
    var ct = c.querySelector('[data-content="' + t.dataset.tab + '"]');
    if (ct) { ct.classList.add('is-active'); ct.querySelectorAll('input[type="hidden"]').forEach(function(h) { h.disabled = false; }); }
  },

  addChip: function(txt) { var ta = this.container.querySelector('.notes-section textarea'); if (ta) ta.value = ta.value ? ta.value + '\n' + txt : txt; },

  loadInitial: function() {
    this.loadFromUrlParams();
    var self = this;
    this.container.querySelectorAll('.option-card.is-selected,.option-row.is-selected').forEach(function(el) {
      var sec = el.closest('.customizer__section'), opt = sec ? sec.dataset.option : null;
      if (opt) self.selections[opt] = { value: el.dataset.value, label: (el.querySelector('.option-card__label,.option-row__title') || {}).textContent || el.dataset.value, price: parseFloat(el.dataset.price) || 0 };
    });
  },

  loadFromUrlParams: function() {
    var params = new URLSearchParams(window.location.search);
    if (params.get('edit') !== 'true') return;
    console.log('Edit mode detected, loading URL params...');
    var banner = document.createElement('div');
    banner.className = 'edit-mode-banner';
    banner.innerHTML = '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg><span>Editing your selections. Make changes and update cart.</span>';
    var pi = this.container.querySelector('.product-info');
    if (pi) pi.prepend(banner);
    var submitBtn = this.container.querySelector('.price-summary__btn');
    if (submitBtn) submitBtn.innerHTML = '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="20 6 9 17 4 12"/></svg> UPDATE CART';
    var self = this;
    params.forEach(function(value, key) {
      if (key === 'edit' || key === 'cart_key' || key === 'id') return;
      var cleanKey = key.replace('properties[', '').replace(']', '');
      self.container.querySelectorAll('.option-card').forEach(function(card) {
        var cv = card.dataset.value || '', clean = value.replace(/\s*\(\+\$[\d.]+\)\s*$/, '');
        if (cv === value || cv === clean) {
          var sec = card.closest('.customizer__section');
          sec.querySelectorAll('.option-card').forEach(function(x) { x.classList.remove('is-selected'); });
          card.classList.add('is-selected');
          var inp = card.querySelector('input'); if (inp) inp.checked = true;
          var opt = sec ? sec.dataset.option : null;
          if (opt) self.selections[opt] = { value: card.dataset.value, label: (card.querySelector('.option-card__label') || {}).textContent || card.dataset.value, price: parseFloat(card.dataset.price) || 0 };
        }
      });
      self.container.querySelectorAll('.option-row').forEach(function(row) {
        var rv = row.dataset.value || '', clean = value.replace(/\s*\(\+\$[\d.]+\)\s*$/, '');
        if (rv === value || rv === clean) {
          var sec = row.closest('.customizer__section');
          sec.querySelectorAll('.option-row').forEach(function(x) { x.classList.remove('is-selected'); });
          row.classList.add('is-selected');
          var inp = row.querySelector('input'); if (inp) inp.checked = true;
          var opt = sec ? sec.dataset.option : null;
          if (opt) self.selections[opt] = { value: row.dataset.value, label: (row.querySelector('.option-row__title') || {}).textContent || row.dataset.value, price: parseFloat(row.dataset.price) || 0 };
        }
      });
      var input = self.container.querySelector('input[name="properties[' + cleanKey + ']"]:not([type="hidden"]):not([type="radio"])');
      if (input) input.value = value;
      var textarea = self.container.querySelector('textarea[name="properties[' + cleanKey + ']"]');
      if (textarea) textarea.value = value;
    });
    this.updatePrice();
  },

  updatePrice: function() {
    var add = 0;
    Object.values(this.selections).forEach(function(s) { add += s.price || 0; });
    var total = this.basePrice + (add * 100);
    var be = this.container.querySelector('[data-base-price]'), ae = this.container.querySelector('[data-additional]'), te = this.container.querySelector('[data-total]');
    if (be) be.textContent = Luxire.Utils.formatMoney(this.basePrice);
    if (ae) { ae.textContent = add > 0 ? '+$' + add.toFixed(2) : '$0.00'; var row = ae.closest('.price-summary__addon'); if (row) row.classList.toggle('hidden', add === 0); }
    if (te) te.textContent = Luxire.Utils.formatMoney(total);
  },

  submit: async function(e) {
    e.preventDefault();
    var btn = this.container.querySelector('.price-summary__btn'); if (!btn) return;
    var orig = btn.innerHTML; btn.disabled = true; btn.innerHTML = 'Adding...';
    var props = {};
    /* 1. Style selections from option cards/rows */
    Object.entries(this.selections).forEach(function(entry) {
      var k = entry[0], s = entry[1];
      var dk = k.split('-').map(function(w) { return w.charAt(0).toUpperCase() + w.slice(1); }).join(' ');
      props[dk] = s.price > 0 ? s.label + ' (+$' + s.price + ')' : s.label;
    });
    /* 2. Collect ALL form properties (measurements, style, instructions) */
    var form = this.container.querySelector('form');
    if (form) {
      form.querySelectorAll('input[name^="properties["], select[name^="properties["], textarea[name^="properties["]').forEach(function(input) {
        if (input.disabled) return;
        if (input.type === 'radio' && !input.checked) return;
        var panel = input.closest('.measurement-panel');
        if (panel && !panel.classList.contains('is-active')) return;
        var val = input.value.trim(); if (!val) return;
        var m = input.name.match(/^properties\[(.+)\]$/); if (!m) return;
        if (!props[m[1]]) props[m[1]] = val;
      });
    }
    var vid = this.container.querySelector('[name="id"]');
    try {
      await Luxire.Cart.add([{ id: parseInt(vid ? vid.value : 0), quantity: 1, properties: props }]);
      btn.innerHTML = '✓ Added!';
      setTimeout(function() { Luxire.CartDrawer.open(); }, 300);
      setTimeout(function() { btn.disabled = false; btn.innerHTML = orig; }, 2500);
    } catch (err) { console.error('Add to cart error:', err); btn.disabled = false; btn.innerHTML = orig; }
  }
};

/* ── Help Panel (lazy-loads help-panel-data.js on first use) ── */
Luxire.HelpPanel = {
  panel: null, overlay: null, helpData: {}, _loaded: false, _loading: false,
  init: function() {
    this.panel = document.querySelector('.help-panel');
    this.overlay = document.querySelector('.help-panel__overlay');
    if (!this.panel) return;
    var self = this;
    document.querySelectorAll('[data-help-close]').forEach(function(b) { b.addEventListener('click', function() { self.close(); }); });
    if (this.overlay) this.overlay.addEventListener('click', function() { self.close(); });
    document.addEventListener('keydown', function(e) { if (e.key === 'Escape') self.close(); });
  },
  _lazyLoad: function(cb) {
    if (this._loaded) { if (cb) cb(); return; }
    if (this._loading) { var self = this; setTimeout(function() { self._lazyLoad(cb); }, 100); return; }
    this._loading = true;
    var self = this;
    var el = document.querySelector('[data-help-data-src]');
    if (!el) { this._loaded = true; this._loading = false; if (cb) cb(); return; }
    var script = document.createElement('script');
    script.src = el.dataset.helpDataSrc;
    script.onload = function() { self._loaded = true; self._loading = false; if (cb) cb(); };
    script.onerror = function() { self._loading = false; if (cb) cb(); };
    document.head.appendChild(script);
  },
  open: function(topic) {
    if (!this.panel) return;
    var self = this;
    if (!this._loaded) {
      /* Show loading state while data loads */
      var title = this.panel.querySelector('.help-panel__title');
      var content = this.panel.querySelector('.help-panel__content');
      if (title) title.textContent = 'Loading...';
      if (content) content.innerHTML = '<p style="text-align:center;padding:40px;color:#999;">Loading help content...</p>';
      this.panel.classList.add('is-open');
      if (this.overlay) this.overlay.classList.add('is-visible');
      document.body.style.overflow = 'hidden';
      this._lazyLoad(function() { self._showContent(topic); });
      return;
    }
    this._showContent(topic);
  },
  _showContent: function(topic) {
    if (!this.panel) return;
    var data = this.helpData[topic] || { title: topic || 'Help', content: '<p>Information not available for this topic.</p>' };
    var title = this.panel.querySelector('.help-panel__title');
    var content = this.panel.querySelector('.help-panel__content');
    if (title) title.textContent = data.title;
    if (content) {
      content.innerHTML = data.content;
      var helpTabs = content.querySelectorAll('[data-helptab]');
      helpTabs.forEach(function(tab) {
        tab.addEventListener('click', function() {
          var tabName = this.getAttribute('data-helptab');
          helpTabs.forEach(function(t) { t.classList.remove('is-active'); });
          this.classList.add('is-active');
          content.querySelectorAll('.help-tab-content').forEach(function(c) { c.classList.remove('is-active'); });
          var tc = content.querySelector('[data-helpcontent="' + tabName + '"]');
          if (tc) tc.classList.add('is-active');
        });
      });
    }
    this.panel.classList.add('is-open');
    if (this.overlay) this.overlay.classList.add('is-visible');
    document.body.style.overflow = 'hidden';
  },
  close: function() {
    if (this.panel) this.panel.classList.remove('is-open');
    if (this.overlay) this.overlay.classList.remove('is-visible');
    document.body.style.overflow = '';
  }
};

/* ── Style Guide Modal (product pages) ── */
Luxire.StyleGuide = {
  init: function() {
    var modal = document.querySelector('[data-style-guide-modal]');
    if (!modal) return;
    function open() { modal.classList.add('is-open'); document.body.style.overflow = 'hidden'; }
    function close() { modal.classList.remove('is-open'); document.body.style.overflow = ''; }
    document.querySelectorAll('[data-open-style-guide]').forEach(function(b) { b.addEventListener('click', open); });
    document.querySelectorAll('[data-close-style-guide]').forEach(function(b) { b.addEventListener('click', close); });
    document.addEventListener('keydown', function(e) { if (e.key === 'Escape' && modal.classList.contains('is-open')) close(); });
  }
};

/* ── Style Choice Toggle - As Shown vs Customize (product pages) ── */
Luxire.StyleChoice = {
  init: function() {
    var choices = document.querySelectorAll('.style-choice');
    var optionsContainer = document.querySelector('[data-customizer-options]');
    var styleInput = document.querySelector('[data-style-input]');
    if (!choices.length) return;
    function getInputs() { return optionsContainer ? optionsContainer.querySelectorAll('input[name^="properties["], select[name^="properties["]') : []; }
    getInputs().forEach(function(input) { input.disabled = true; });
    choices.forEach(function(choice) {
      choice.addEventListener('click', function() {
        var value = this.querySelector('input').value, isCustomize = value === 'customize';
        choices.forEach(function(c) { c.classList.remove('is-selected'); });
        this.classList.add('is-selected');
        this.querySelector('input').checked = true;
        if (styleInput) styleInput.value = value;
        if (optionsContainer) { optionsContainer.style.display = isCustomize ? 'block' : 'none'; getInputs().forEach(function(input) { input.disabled = !isCustomize; }); }
      });
    });
  }
};

/* ── Measurement Method Switcher (product pages) ── */
Luxire.MeasurementMethod = {
  init: function() {
    var methods = document.querySelectorAll('.measurement-method');
    var panels = document.querySelectorAll('.measurement-panel');
    if (!methods.length) return;
    methods.forEach(function(method) {
      method.addEventListener('click', function() {
        var target = this.dataset.method;
        methods.forEach(function(m) { m.classList.remove('is-selected'); });
        this.classList.add('is-selected');
        var radio = this.querySelector('input[type="radio"]'); if (radio) radio.checked = true;
        panels.forEach(function(panel) {
          var isTarget = panel.dataset.panel === target;
          panel.classList.toggle('is-active', isTarget);
          var h = panel.querySelector('input[name="properties[Measurement Method]"]'); if (h) h.disabled = !isTarget;
        });
      });
    });
  }
};

/* ── FAQ Accordion ── */
Luxire.FAQ = {
  init: function() {
    document.querySelectorAll('.faq-item__question').forEach(function(b) {
      b.addEventListener('click', function() { b.closest('.faq-item').classList.toggle('is-open'); });
    });
  }
};

/* ── Product Info Tabs ── */
Luxire.ProductTabs = {
  init: function() {
    var tabs = document.querySelectorAll('.product-tabs__tab');
    var panels = document.querySelectorAll('.product-tabs__panel');
    if (!tabs.length) return;
    
    tabs.forEach(function(tab) {
      tab.addEventListener('click', function() {
        var target = this.dataset.tab;
        
        // Update tabs
        tabs.forEach(function(t) { t.classList.remove('is-active'); });
        this.classList.add('is-active');
        
        // Update panels
        panels.forEach(function(panel) {
          panel.classList.toggle('is-active', panel.dataset.panel === target);
        });
      });
    });
  }
};

/* ── Recommendation Slider ── */
Luxire.RecommendationSlider = {
  init: function() {
    var sliders = document.querySelectorAll('.recommendations-slider');
    sliders.forEach(function(slider) {
      var track = slider.querySelector('[data-slider-track]');
      var prevBtn = slider.querySelector('[data-slider-prev]');
      var nextBtn = slider.querySelector('[data-slider-next]');
      
      if (!track) return;
      
      var scrollAmount = 240;
      
      if (prevBtn) {
        prevBtn.addEventListener('click', function() {
          track.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
        });
      }
      
      if (nextBtn) {
        nextBtn.addEventListener('click', function() {
          track.scrollBy({ left: scrollAmount, behavior: 'smooth' });
        });
      }
    });
  }
};

/* ── Recently Viewed Products ── */
Luxire.RecentlyViewed = {
  storageKey: 'luxire_recently_viewed',
  maxItems: 8,
  
  init: function() {
    this.trackCurrentProduct();
    this.displayRecentlyViewed();
  },
  
  trackCurrentProduct: function() {
    var productJson = document.querySelector('[data-product-json]');
    if (!productJson) return;
    
    try {
      var product = JSON.parse(productJson.textContent);
      var viewed = this.getViewed();
      
      // Remove if already exists
      viewed = viewed.filter(function(p) { return p.id !== product.id; });
      
      // Add to beginning
      viewed.unshift({
        id: product.id,
        title: product.title,
        url: product.url,
        image: product.featured_image,
        price: product.price,
        type: product.type,
        vendor: product.vendor
      });
      
      // Limit to max items
      viewed = viewed.slice(0, this.maxItems);
      
      localStorage.setItem(this.storageKey, JSON.stringify(viewed));
    } catch (e) {
      console.log('Could not track product:', e);
    }
  },
  
  getViewed: function() {
    try {
      var stored = localStorage.getItem(this.storageKey);
      return stored ? JSON.parse(stored) : [];
    } catch (e) {
      return [];
    }
  },
  
  displayRecentlyViewed: function() {
    var container = document.querySelector('[data-recently-viewed]');
    var grid = document.querySelector('[data-recently-viewed-products]');
    if (!container || !grid) return;
    
    var viewed = this.getViewed();
    var productJson = document.querySelector('[data-product-json]');
    var currentId = null;
    
    if (productJson) {
      try {
        currentId = JSON.parse(productJson.textContent).id;
      } catch (e) {}
    }
    
    // Filter out current product
    viewed = viewed.filter(function(p) { return p.id !== currentId; });
    
    if (viewed.length < 2) {
      container.style.display = 'none';
      return;
    }
    
    container.style.display = 'block';
    
    var html = '';
    viewed.slice(0, 4).forEach(function(product) {
      var priceFormatted = '$' + (product.price / 100).toFixed(2);
      html += '<div class="recommendation-card">' +
        '<a href="' + product.url + '" class="recommendation-card__image">' +
        '<img src="' + product.image + '" alt="' + product.title + '" loading="lazy">' +
        '</a>' +
        '<div class="recommendation-card__info">' +
        '<span class="recommendation-card__type">' + (product.type || '') + '</span>' +
        '<h3 class="recommendation-card__title"><a href="' + product.url + '">' + product.title.substring(0, 45) + '</a></h3>' +
        '<div class="recommendation-card__price">' + priceFormatted + '</div>' +
        '<a href="' + product.url + '" class="recommendation-card__btn">View Details</a>' +
        '</div></div>';
    });
    
    grid.innerHTML = html;
  }
};

/* ── Quick Add to Cart ── */
Luxire.QuickAdd = {
  init: function() {
    document.querySelectorAll('.recommendation-card__quick-add').forEach(function(btn) {
      btn.addEventListener('click', function() {
        var variantId = this.dataset.productId;
        if (!variantId) return;
        
        var originalText = this.innerHTML;
        this.innerHTML = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="spin"><circle cx="12" cy="12" r="10"/></svg> Adding...';
        this.disabled = true;
        
        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: variantId, quantity: 1 })
        })
        .then(function(response) { return response.json(); })
        .then(function(data) {
          btn.innerHTML = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="20 6 9 17 4 12"/></svg> Added!';
          setTimeout(function() {
            btn.innerHTML = originalText;
            btn.disabled = false;
          }, 2000);
          Luxire.Cart.updateCount();
          if (Luxire.CartDrawer && Luxire.CartDrawer.open) {
            Luxire.CartDrawer.open();
          }
        })
        .catch(function(error) {
          btn.innerHTML = 'Error';
          setTimeout(function() {
            btn.innerHTML = originalText;
            btn.disabled = false;
          }, 2000);
        });
      });
    });
  }
};

/* ── Collection Filters ── */
Luxire.Filters = {
  init: function() {
    var sb = document.querySelector('.filter-sidebar,[data-filters]');
    if (!sb) return;
    document.querySelectorAll('[data-filter-open],[data-filter-toggle]').forEach(function(b) {
      b.addEventListener('click', function() { sb.classList.add('is-open'); sb.setAttribute('aria-hidden', 'false'); document.body.style.overflow = 'hidden'; });
    });
    document.querySelectorAll('[data-filter-close]').forEach(function(b) {
      b.addEventListener('click', function() { sb.classList.remove('is-open'); sb.setAttribute('aria-hidden', 'true'); document.body.style.overflow = ''; });
    });
    sb.querySelectorAll('.filter-group__toggle').forEach(function(toggle) {
      toggle.addEventListener('click', function() { var exp = toggle.getAttribute('aria-expanded') !== 'false'; toggle.setAttribute('aria-expanded', !exp); });
    });
    var sortSel = document.querySelector('[data-sort-select]');
    if (sortSel) sortSel.addEventListener('change', function(e) { var url = new URL(window.location); url.searchParams.set('sort_by', e.target.value); window.location = url; });
    document.querySelectorAll('[data-view]').forEach(function(btn) {
      btn.addEventListener('click', function() {
        document.querySelectorAll('[data-view]').forEach(function(b) { b.classList.remove('is-active'); });
        btn.classList.add('is-active');
        var grid = document.querySelector('[data-product-grid]');
        if (grid) grid.classList.toggle('product-grid--list', btn.dataset.view === 'list');
      });
    });
  }
};

/* ── Floating Cart Bar (product pages — shows when main button scrolls away) ── */
Luxire.FloatingCartBar = {
  bar: null, mainBtn: null,
  init: function() {
    this.bar = document.getElementById('floatingCartBar');
    if (!this.bar) return;
    this.mainBtn = document.querySelector('.price-summary__btn, .price-summary button[type="submit"]');
    if (!this.mainBtn) return;
    var self = this;
    var observer = new IntersectionObserver(function(entries) {
      entries.forEach(function(entry) {
        self.bar.setAttribute('aria-hidden', entry.isIntersecting ? 'true' : 'false');
      });
    }, { threshold: 0 });
    observer.observe(this.mainBtn);
    var addBtn = document.getElementById('floatingAddToCart');
    if (addBtn) addBtn.addEventListener('click', function() { self.mainBtn.click(); });
  }
};

/* ── Initialize ── */
document.addEventListener('DOMContentLoaded', function() {
  Luxire.StickyHeader.init();
  Luxire.MobileMenu.init();
  Luxire.Search.init();
  Luxire.CartDrawer.init();
  Luxire.Slideshow.init();
  Luxire.ProductGallery.init();
  Luxire.QuantitySelector.init();
  Luxire.Customizer.init();
  Luxire.StyleGuide.init();
  Luxire.StyleChoice.init();
  Luxire.MeasurementMethod.init();
  Luxire.HelpPanel.init();
  Luxire.FloatingCartBar.init();
  Luxire.FAQ.init();
  Luxire.ProductTabs.init();
  Luxire.RecommendationSlider.init();
  Luxire.RecentlyViewed.init();
  Luxire.QuickAdd.init();
  Luxire.Filters.init();
  Luxire.Cart.updateCount();
});
})();
